<template><div><component-combinator component-name="DtRadioGroup" /><h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-radio-group
  model-value=&quot;&quot;
  name=&quot;fruits-radio-group-01&quot;
  legend=&quot;Fruits&quot;
&gt;
  &lt;dt-radio value=&quot;apple&quot;&gt;&lt;span &gt;Apple&lt;/span&gt;&lt;/dt-radio&gt;
  &lt;dt-radio value=&quot;banana&quot;&gt;&lt;span &gt;Banana&lt;/span&gt;&lt;/dt-radio&gt;
  &lt;dt-radio value=&quot;other&quot;&gt;&lt;span &gt;Other&lt;/span&gt;&lt;/dt-radio&gt;
&lt;/dt-radio-group&gt;'>
<dt-radio-group
  model-value=""
  name="fruits-radio-group-01"
  legend="Fruits"
>
  <dt-radio value="apple"><span >Apple</span></dt-radio>
  <dt-radio value="banana"><span >Banana</span></dt-radio>
  <dt-radio value="other"><span >Other</span></dt-radio>
</dt-radio-group>
</code-example>
<h3 id="with-options" tabindex="-1" class="d-docsite--header-3">With Options <a class="header-anchor d-link d-docsite--link d-link" href="#with-options" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Passing in Radio components programmatically using an options object.</p>
<code-example source-code='&lt;dt-radio-group
  v-model=&quot;selectedFruits&quot;
  name=&quot;fruits-radio-group&quot;
  legend=&quot;Fruits&quot;
&gt;
  &lt;dt-radio
    v-for=&quot;option in options&quot;
    :key=&quot;option.value&quot;
    :value=&quot;option.value&quot;
  &gt;
    &lt;span&gt;{{ option.label }}&lt;/span&gt;
  &lt;/dt-radio&gt;
&lt;/dt-radio-group&gt;'>
<dt-radio-group
  v-model="selectedFruits"
  name="fruits-radio-group"
  legend="Fruits"
>
  <dt-radio
    v-for="option in options"
    :key="option.value"
    :value="option.value"
  >
    <span>{{ option.label }}</span>
  </dt-radio>
</dt-radio-group>
</code-example>
<h3 id="without-legend" tabindex="-1" class="d-docsite--header-3">Without Legend <a class="header-anchor d-link d-docsite--link d-link" href="#without-legend" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When no legend is provided it is expected that an <code v-pre>aria-label</code> is passed into the component.</p>
<code-example source-code='&lt;dt-radio-group
  name=&quot;fruits-radio-group&quot;
  aria-label=&quot;Fruits&quot;
&gt;
  &lt;dt-radio value=&quot;pear&quot;&gt;Pear&lt;/dt-radio&gt;
  &lt;dt-radio value=&quot;kiwi&quot;&gt;Kiwi&lt;/dt-radio&gt;
&lt;/dt-radio-group&gt;'>
<dt-radio-group
  name="fruits-radio-group"
  aria-label="Fruits"
>
  <dt-radio value="pear">Pear</dt-radio>
  <dt-radio value="kiwi">Kiwi</dt-radio>
</dt-radio-group>
</code-example>
<h3 id="with-slotted-legend" tabindex="-1" class="d-docsite--header-3">With Slotted Legend <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-legend" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The legend can also be passed by slot.</p>
<code-example source-code='&lt;dt-radio-group
  name=&quot;fruits-radio-group&quot;
&gt;
  &lt;dt-radio value=&quot;pear&quot;&gt;Pear&lt;/dt-radio&gt;
  &lt;dt-radio value=&quot;kiwi&quot;&gt;Kiwi&lt;/dt-radio&gt;
  &lt;template #legend&gt;
    Fruits
  &lt;/template&gt;
&lt;/dt-radio-group&gt;'>
<dt-radio-group
  name="fruits-radio-group"
>
  <dt-radio value="pear">Pear</dt-radio>
  <dt-radio value="kiwi">Kiwi</dt-radio>
  <template #legend>
    Fruits
  </template>
</dt-radio-group>
</code-example>
<h3 id="with-event-hander" tabindex="-1" class="d-docsite--header-3">With Event Hander <a class="header-anchor d-link d-docsite--link d-link" href="#with-event-hander" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The event handler is only needed if you need to do additional processing. The v-model is automatically updated.</p>
<code-example source-code='&lt;dt-radio-group
  v-model=&quot;selectedFruits&quot;
  name=&quot;fruits-radio-group&quot;
  legend=&quot;Fruits&quot;
  @update:model-value=&quot;onInput&quot;
&gt;
  &lt;dt-radio value=&quot;pear&quot;&gt;Pear&lt;/dt-radio&gt;
  &lt;dt-radio value=&quot;kiwi&quot;&gt;Kiwi&lt;/dt-radio&gt;
&lt;/dt-radio-group&gt;'>
<dt-radio-group
  v-model="selectedFruits"
  name="fruits-radio-group"
  legend="Fruits"
  @update:model-value="onInput"
>
  <dt-radio value="pear">Pear</dt-radio>
  <dt-radio value="kiwi">Kiwi</dt-radio>
</dt-radio-group>
</code-example>
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;div&gt;
  &lt;dt-radio-group
    name=&quot;radio-group-with-positive-message&quot;
    legend=&quot;With Positive Message&quot;
    :messages=&#39;[{&quot;message&quot;:&quot;Positive validation message&quot;,&quot;type&quot;:&quot;positive&quot;}]&#39;
  &gt;
    &lt;dt-radio value=&quot;apple&quot;&gt;&lt;span &gt;Apple&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;banana&quot;&gt;&lt;span &gt;Banana&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;other&quot;&gt;&lt;span &gt;Other&lt;/span&gt;&lt;/dt-radio&gt;
  &lt;/dt-radio-group&gt;
&lt;/div&gt;
&lt;div&gt;
  &lt;dt-radio-group
    name=&quot;radio-group-with-warning-message&quot;
    legend=&quot;With Warning Message&quot;
    :messages=&#39;[{&quot;message&quot;:&quot;Warning validation message&quot;,&quot;type&quot;:&quot;warning&quot;}]&#39;
  &gt;
    &lt;dt-radio value=&quot;apple&quot;&gt;&lt;span &gt;Apple&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;banana&quot;&gt;&lt;span &gt;Banana&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;other&quot;&gt;&lt;span &gt;Other&lt;/span&gt;&lt;/dt-radio&gt;
  &lt;/dt-radio-group&gt;
&lt;/div&gt;
&lt;div&gt;
  &lt;dt-radio-group
    name=&quot;radio-group-with-critical-message&quot;
    legend=&quot;With Critical Message&quot;
    :messages=&#39;[{&quot;message&quot;:&quot;Critical validation message&quot;,&quot;type&quot;:&quot;critical&quot;}]&#39;
  &gt;
    &lt;dt-radio value=&quot;apple&quot;&gt;&lt;span &gt;Apple&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;banana&quot;&gt;&lt;span &gt;Banana&lt;/span&gt;&lt;/dt-radio&gt;
    &lt;dt-radio value=&quot;other&quot;&gt;&lt;span &gt;Other&lt;/span&gt;&lt;/dt-radio&gt;
  &lt;/dt-radio-group&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200">
  <div>
    <dt-radio-group
      name="radio-group-with-positive-message"
      legend="With Positive Message"
      :messages='[{"message":"Positive validation message","type":"positive"}]'
    >
      <dt-radio value="apple"><span >Apple</span></dt-radio>
      <dt-radio value="banana"><span >Banana</span></dt-radio>
      <dt-radio value="other"><span >Other</span></dt-radio>
    </dt-radio-group>
  </div>
  <div>
    <dt-radio-group
      name="radio-group-with-warning-message"
      legend="With Warning Message"
      :messages='[{"message":"Warning validation message","type":"warning"}]'
    >
      <dt-radio value="apple"><span >Apple</span></dt-radio>
      <dt-radio value="banana"><span >Banana</span></dt-radio>
      <dt-radio value="other"><span >Other</span></dt-radio>
    </dt-radio-group>
  </div>
  <div>
    <dt-radio-group
      name="radio-group-with-critical-message"
      legend="With Critical Message"
      :messages='[{"message":"Critical validation message","type":"critical"}]'
    >
      <dt-radio value="apple"><span >Apple</span></dt-radio>
      <dt-radio value="banana"><span >Banana</span></dt-radio>
      <dt-radio value="other"><span >Other</span></dt-radio>
    </dt-radio-group>
  </div>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="radiogroup" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Radio Groups are typically paired with a legend which identifies the group. If no legend is provided then it is expected
that an <code v-pre>aria-label</code> will be given in order to provide an invisible label to screen readers.</p>
</div></template>


<script setup>
import { ref } from 'vue';
const options = [
  { label: 'Apple', value: 'apple' },
  { label: 'Banana', value: 'banana' },
  { label: 'Other', value: 'other' }
];
const selectedFruits = ref('apple');

const onInput = () => {};

</script>